/** 应用根组件的 reporter 加载入口，负责 CDN 资源去重与运行时挂载 */
import { useEffect, useLayoutEffect } from 'react';

/** CDN 产物注册到宿主页面的挂载和卸载接口 */
export type JiraReporterRuntime = {
    version: string;
    mount: () => void;
    unmount: () => void;
};

/** CDN 清单中的版本及同目录 JS/CSS 资源路径 */
export type JiraReporterManifest = {
    version: string;
    script: string;
    style: string;
};

/** 所有接入应用共用的正式 CDN 清单地址 */
export const JIRA_REPORTER_MANIFEST_URL = 'https://pc.meitudata.com/x-design/jira-reporter/manifest.json';
/** 非线上环境通过 URL 临时覆盖清单地址的查询参数 */
export const JIRA_REPORTER_MANIFEST_OVERRIDE_PARAM = 'jiraReporterManifest';
/** 非线上环境保存本地清单地址的存储键 */
export const JIRA_REPORTER_MANIFEST_OVERRIDE_STORAGE_KEY = 'MEITU_JIRA_REPORTER_MANIFEST_URL';

const RUNTIME_KEY = '__MEITU_JIRA_REPORTER_RUNTIME__';
const LOAD_PROMISE_KEY = '__MEITU_JIRA_REPORTER_LOAD_PROMISE__';
const STYLE_URL_KEY = '__MEITU_JIRA_REPORTER_STYLE_URL__';
const STYLE_WATCHER_KEY = '__MEITU_JIRA_REPORTER_STYLE_WATCHER__';
const SHOULD_BE_MOUNTED_KEY = '__MEITU_JIRA_REPORTER_SHOULD_BE_MOUNTED__';
const RESOURCE_ATTRIBUTE = 'data-meitu-jira-reporter-resource';
const RESOURCE_STATE_ATTRIBUTE = 'data-meitu-jira-reporter-resource-state';
const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type JiraReporterGlobal = typeof globalThis & {
    [RUNTIME_KEY]?: JiraReporterRuntime;
    [LOAD_PROMISE_KEY]?: Promise<void>;
    [STYLE_URL_KEY]?: string;
    [STYLE_WATCHER_KEY]?: MutationObserver;
    [SHOULD_BE_MOUNTED_KEY]?: boolean;
};

const getGlobal = () => globalThis as JiraReporterGlobal;

const withCacheBuster = (url: string) => {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}_=${Date.now()}`;
};

const resolveResourceUrl = (manifestUrl: string, resourceUrl: string) => {
    try {
        return new URL(resourceUrl, manifestUrl).href;
    } catch {
        return '';
    }
};

const getManifestUrl = () => {
    if (typeof window === 'undefined') return JIRA_REPORTER_MANIFEST_URL;

    const fromQuery = new URLSearchParams(window.location.search).get(JIRA_REPORTER_MANIFEST_OVERRIDE_PARAM)?.trim();
    if (fromQuery) return fromQuery;

    try {
        return (
            window.localStorage.getItem(JIRA_REPORTER_MANIFEST_OVERRIDE_STORAGE_KEY)?.trim() ||
            JIRA_REPORTER_MANIFEST_URL
        );
    } catch {
        return JIRA_REPORTER_MANIFEST_URL;
    }
};

const isValidManifest = (value: unknown): value is JiraReporterManifest => {
    if (!value || typeof value !== 'object') return false;

    const manifest = value as Partial<JiraReporterManifest>;
    return (
        typeof manifest.version === 'string' &&
        /^[0-9]+\.[0-9]+\.[0-9]+(?:[-+][\w.-]+)?$/.test(manifest.version) &&
        typeof manifest.script === 'string' &&
        typeof manifest.style === 'string'
    );
};

const isStylesheetReady = (element: HTMLLinkElement) =>
    element.isConnected &&
    !element.disabled &&
    element.relList.contains('stylesheet') &&
    (element.getAttribute(RESOURCE_STATE_ATTRIBUTE) === 'loaded' || element.sheet !== null);

const waitForResource = (element: HTMLElement, url: string) =>
    new Promise<void>((resolve, reject) => {
        const cleanup = () => {
            element.removeEventListener('load', handleLoad);
            element.removeEventListener('error', handleError);
        };
        const handleLoad = () => {
            cleanup();
            element.setAttribute(RESOURCE_STATE_ATTRIBUTE, 'loaded');
            resolve();
        };
        const handleError = () => {
            cleanup();
            element.remove();
            reject(new Error(`Failed to load jira-reporter resource: ${url}`));
        };

        element.addEventListener('load', handleLoad);
        element.addEventListener('error', handleError);
    });

const loadResource = (tagName: 'link' | 'script', url: string, attributes: Record<string, string>) => {
    const existing = [...document.querySelectorAll<HTMLElement>(tagName)].find(
        (element) => element.getAttribute(RESOURCE_ATTRIBUTE) === url,
    );
    if (existing) {
        if (tagName === 'link' && isStylesheetReady(existing as HTMLLinkElement)) {
            return Promise.resolve();
        }
        if (tagName === 'script' && existing.getAttribute(RESOURCE_STATE_ATTRIBUTE) === 'loaded') {
            return Promise.resolve();
        }

        const existingLink = tagName === 'link' ? (existing as HTMLLinkElement) : null;
        if (
            existing.getAttribute(RESOURCE_STATE_ATTRIBUTE) === 'loaded' ||
            (existingLink &&
                (existingLink.disabled || !existingLink.relList.contains('stylesheet') || existingLink.href !== url))
        ) {
            existing.remove();
        } else {
            // A second copy of the loader can discover the element while the first
            // one is still downloading it. Wait for the real load event instead of
            // allowing the runtime to mount without its stylesheet.
            return waitForResource(existing, url);
        }
    }

    const element = document.createElement(tagName);
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    element.setAttribute(RESOURCE_ATTRIBUTE, url);
    element.setAttribute(RESOURCE_STATE_ATTRIBUTE, 'loading');
    const loading = waitForResource(element, url);
    document.head.appendChild(element);
    return loading;
};

const getLoadedStylesheet = (styleUrl: string) =>
    [...document.querySelectorAll<HTMLLinkElement>('link')].find(
        (element) => element.getAttribute(RESOURCE_ATTRIBUTE) === styleUrl && isStylesheetReady(element),
    );

const watchStylesheet = (styleUrl: string) => {
    const global = getGlobal();
    global[STYLE_URL_KEY] = styleUrl;
    global[STYLE_WATCHER_KEY]?.disconnect();

    const watcher = new MutationObserver(() => {
        const currentGlobal = getGlobal();
        const currentStyleUrl = currentGlobal[STYLE_URL_KEY];
        if (currentGlobal[SHOULD_BE_MOUNTED_KEY] && currentStyleUrl && !getLoadedStylesheet(currentStyleUrl)) {
            void loadJiraReporter();
        }
    });
    watcher.observe(document.head, {
        childList: true,
        attributes: true,
        attributeFilter: ['disabled', 'href', 'rel'],
    });
    global[STYLE_WATCHER_KEY] = watcher;
};

const fetchManifest = async (manifestUrl: string): Promise<JiraReporterManifest> => {
    const response = await fetch(withCacheBuster(manifestUrl), {
        cache: 'no-store',
        credentials: 'omit',
    });

    if (!response.ok) {
        throw new Error(`Failed to load jira-reporter manifest: ${response.status}`);
    }

    const manifest: unknown = await response.json();
    if (!isValidManifest(manifest)) {
        throw new Error('Invalid jira-reporter manifest');
    }

    return manifest;
};

const loadRuntime = async () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const manifestUrl = getManifestUrl();
    const manifest = await fetchManifest(manifestUrl);
    const styleUrl = resolveResourceUrl(manifestUrl, manifest.style);
    const scriptUrl = resolveResourceUrl(manifestUrl, manifest.script);
    if (!styleUrl || !scriptUrl) throw new Error('Invalid jira-reporter resource URL');

    let runtime = getGlobal()[RUNTIME_KEY];
    const resourceLoadings: Promise<void>[] = [
        loadResource('link', styleUrl, {
            rel: 'stylesheet',
            href: styleUrl,
            fetchPriority: 'high',
        }),
    ];
    if (!runtime || runtime.version !== manifest.version) {
        resourceLoadings.push(
            loadResource('script', scriptUrl, {
                src: scriptUrl,
                async: 'true',
                fetchPriority: 'high',
            }),
        );
    }
    await Promise.all(resourceLoadings);
    runtime = getGlobal()[RUNTIME_KEY];

    if (!runtime || runtime.version !== manifest.version) {
        throw new Error('jira-reporter runtime did not initialize');
    }

    watchStylesheet(styleUrl);
    runtime.mount();
};

/**
 * 加载并挂载最新的 standalone reporter
 *
 * @returns 加载流程结束的 Promise；失败时静默结束，避免影响宿主应用
 */
export const loadJiraReporter = (): Promise<void> => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return Promise.resolve();

    const global = getGlobal();
    global[SHOULD_BE_MOUNTED_KEY] = true;
    const styleUrl = global[STYLE_URL_KEY];
    if (global[RUNTIME_KEY] && styleUrl && getLoadedStylesheet(styleUrl)) {
        global[RUNTIME_KEY]?.mount();
        return Promise.resolve();
    }

    if (!global[LOAD_PROMISE_KEY]) {
        const loading = loadRuntime()
            .catch(() => {
                // Reporter loading must never block or break the host application.
            })
            .finally(() => {
                if (global[LOAD_PROMISE_KEY] === loading) global[LOAD_PROMISE_KEY] = undefined;
            });
        global[LOAD_PROMISE_KEY] = loading;
    }

    return global[LOAD_PROMISE_KEY];
};

/**
 * 卸载 standalone reporter，同时保留已缓存的静态资源
 *
 * 调用后会卸载 React 根节点及其渲染内容。
 */
export const unloadJiraReporter = () => {
    const global = getGlobal();
    global[SHOULD_BE_MOUNTED_KEY] = false;
    global[RUNTIME_KEY]?.unmount();
};

/**
 * 在应用根组件提交的浏览器布局阶段立即请求 standalone reporter 资源
 *
 * 依赖浏览器环境及全局 reporter runtime，不等待页面 load 事件或额外定时器。
 *
 * @returns 不渲染宿主 DOM 内容
 */
export const JiraReporterLoader = () => {
    useBrowserLayoutEffect(() => {
        void loadJiraReporter();
    }, []);

    return null;
};
